import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit, AlertCircle, Network } from 'lucide-react';
import {
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DataTable,
  TruncatedText,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type { ValidatedDepartment } from '@bliks-io/contracts';

export interface DraftDepartment extends ValidatedDepartment {
  id: string; // Local UUID for React keys
}

interface DepartmentDraftTableProps {
  drafts: DraftDepartment[];
  onEdit: (draft: DraftDepartment) => void;
  onDelete: (id: string) => void;
}

/**
 * Calculate department depth for visual hierarchy
 * Recursively finds depth based on parent relationships within the batch
 */
function calculateDepartmentDepth(
  dept: DraftDepartment,
  allDepts: DraftDepartment[],
  visited = new Set<number>()
): number {
  // Prevent circular reference infinite loops
  if (visited.has(dept.rowNumber)) return 0;
  visited.add(dept.rowNumber);

  if (!dept.data.parentAbbreviation) return 0;

  const parent = allDepts.find(
    (d) => d.data.abbreviation.toLowerCase() === dept.data.parentAbbreviation?.toLowerCase()
  );

  if (!parent) return 0;

  return 1 + calculateDepartmentDepth(parent, allDepts, visited);
}

/**
 * Get parent source indicator icon with tooltip
 */
function ParentSourceIndicator({ dept }: { dept: DraftDepartment }) {
  const { t } = useTranslation();

  if (!dept.data.parentAbbreviation) return null;

  // Check if there's a parent-related error
  const hasParentError = dept.errors?.some((e) => e.toLowerCase().includes('parent'));

  if (hasParentError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-danger text-lg">🔴</span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{t('organization.departments.bulkUploadPage.parentNotFound')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (dept.data.parentSource === 'database') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-positive text-lg">🟢</span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">
              {t('organization.departments.bulkUploadPage.parentInDatabase')}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (dept.data.parentSource === 'batch') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-info text-lg">🔵</span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{t('organization.departments.bulkUploadPage.parentInBatch')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}

export function DepartmentDraftTable({ drafts, onEdit, onDelete }: DepartmentDraftTableProps) {
  const { t } = useTranslation();

  const columns = useMemo<DataTableColumnDef<DraftDepartment>[]>(
    () => [
      // Status column
      {
        id: 'status',
        header: t('common.Status'),
        size: 60,
        cell: ({ row }) => {
          const draft = row.original;

          if (draft.isValid) {
            return (
              <div className="flex items-center justify-center text-positive">
                <span className="text-lg">✓</span>
              </div>
            );
          }

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center cursor-help">
                    <AlertCircle className="h-5 w-5 text-warning" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <div className="space-y-1">
                    <p className="font-semibold text-sm">
                      {t('organization.departments.bulkUploadPage.validationErrors')}:
                    </p>
                    <ul className="text-xs space-y-0.5">
                      {draft.errors?.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }
      },
      // Row number column
      {
        accessorKey: 'rowNumber',
        header: t('organization.departments.bulkUploadPage.rowNumber'),
        size: 80,
        cell: ({ row }) => (
          <div className="text-muted-foreground font-mono text-sm">
            #{row.getValue('rowNumber')}
          </div>
        )
      },
      // Name column
      {
        accessorFn: (row) => row.data.name,
        id: 'name',
        header: t('common.Name'),
        cell: ({ row }) => {
          const draft = row.original;
          const depth = calculateDepartmentDepth(draft, drafts);
          const hasParent = !!draft.data.parentAbbreviation;

          return (
            <div className="flex items-center gap-1.5" style={{ paddingLeft: `${depth * 24}px` }}>
              {hasParent && depth > 0 && (
                <Network className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              )}
              <span className="font-medium">{draft.data.name}</span>
            </div>
          );
        }
      },
      // Abbreviation column
      {
        accessorFn: (row) => row.data.abbreviation,
        id: 'abbreviation',
        header: t('organizations.Abbreviation'),
        cell: ({ row }) => {
          const abbr = row.original.data.abbreviation;
          return (
            <Badge className="bg-fill-neutral text-white border-transparent hover:bg-fill-neutral">
              {abbr}
            </Badge>
          );
        }
      },
      // Description column
      {
        accessorFn: (row) => row.data.description,
        id: 'description',
        header: t('common.Description'),
        cell: ({ row }) => (
          <TruncatedText text={row.original.data.description} className="max-w-[300px]" />
        )
      },
      // Parent abbreviation column with source indicator
      {
        accessorFn: (row) => row.data.parentAbbreviation,
        id: 'parent',
        header: t('organization.departments.parentDepartment'),
        cell: ({ row }) => {
          const draft = row.original;
          const parentAbbr = draft.data.parentAbbreviation;

          if (!parentAbbr) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <div className="flex items-center gap-2">
              <ParentSourceIndicator dept={draft} />
              <span className="font-mono text-sm">{parentAbbr}</span>
            </div>
          );
        }
      },
      // Manager email column
      {
        accessorFn: (row) => row.data.managerEmail,
        id: 'managerEmail',
        header: t('organization.departments.manager'),
        cell: ({ row }) => (
          <TruncatedText text={row.original.data.managerEmail} className="max-w-[200px] text-sm" />
        )
      },
      // Creation order column
      {
        id: 'creationOrder',
        header: t('organization.departments.bulkUploadPage.creationOrder'),
        size: 100,
        cell: () => {
          // This will be set by the parent component based on the validation response
          return <div className="text-center text-muted-foreground font-mono text-sm">-</div>;
        }
      },
      // Actions column
      {
        id: 'actions',
        header: t('common.Actions'),
        size: 120,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="small" onClick={() => onEdit(row.original)}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="small" onClick={() => onDelete(row.original.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    ],
    [t, drafts, onEdit, onDelete]
  );

  if (drafts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 border border-dashed rounded-lg">
        <p className="text-muted-foreground">
          {t('organization.departments.bulkUploadPage.noDrafts')}
        </p>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={drafts}
      isLoading={false}
      emptyMessage={t('organization.departments.bulkUploadPage.noDrafts')}
    />
  );
}
